/**
 * A "read more" component which uses CSS line clamp to restrict content length.
 *
 * Example HTML:
 * <read-more data-read-more="{%- render "format-translation", namespace: "general.read_more", fallback: "Read more" -%}" data-read-less="{%- render "format-translation", namespace: "products.product.read_less", fallback: "Read less" -%}">
 *   <div data-content class="collection-banner__text-inner">
 *     {{- collection_banner.description | metafield_tag -}}
 *   </div>
 *   <button data-toggle>
 *     {%- render "format-translation", namespace: "general.read_more", fallback: "Read more" -%}
 *   </button>
 * </read-more>
 */
if (!customElements.get('read-more')) {
  customElements.define(
    'read-more',
    class ReadMore extends HTMLElement {
      /**
       * Initializes component properties based on dataset attributes or global fallbacks,
       * and binds event handler contexts.
       */
      constructor() {
        super();
        this.readMore = this.dataset.readMore || window.strings?.readMore || 'Read more';
        this.readLess = this.dataset.readLess || window.strings?.readLess || 'Read less';

        // Store bound methods so they can be referenced when adding/removing listeners
        this.boundToggle = this.toggle.bind(this);
        this.boundCheck = this.check.bind(this);
      }

      /**
       * Lifecycle callback invoked when the element is appended to the DOM.
       * Verifies required child elements exist before initializing the component.
       */
      connectedCallback() {
        this.content = this.querySelector('[data-content]');
        this.button = this.querySelector('[data-toggle]');
        if (!this.content || !this.button) return;

        // Set initial state
        this.button.setAttribute('aria-expanded', 'false');

        // Check if content is overflowing
        this.check();

        // Add event listeners
        this.addEventListeners();
      }

      /**
       * Lifecycle callback invoked when the element is removed from the DOM.
       * Cleans up event listeners to prevent memory leaks.
       */
      disconnectedCallback() {
        if (this.button) this.button.removeEventListener('click', this.boundToggle);
        window.removeEventListener('resize', this.boundCheck);
      }

      /**
       * Binds event listeners for user interactions (button clicks) and window resizing.
       */
      addEventListeners() {
        this.button.addEventListener('click', this.boundToggle);
        window.addEventListener('resize', this.boundCheck);
      }

      /**
       * Evaluates whether the content is overflowing its constrained height.
       * Toggles the visibility of the "Read more" button accordingly.
       */
      check() {
        // 1. Store current state and temporarily collapse to accurately measure the clamped height
        const isExpanded = this.button.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
          this.button.removeAttribute('aria-expanded');
        }

        // 2. Check if the text overflows its container
        // scrollHeight represents the total height of the text, clientHeight is the visible restricted height
        const isOverflowing = this.content.scrollHeight > this.content.clientHeight;

        // 3. Toggle the hidden class (hides the button if there is no overflow)
        this.button.classList.toggle('hidden', !isOverflowing);

        // 4. Restore the expanded state if it was previously open
        if (isExpanded) {
          this.button.setAttribute('aria-expanded', 'true');
        }
      }

      /**
       * Toggles the `aria-expanded` state on the content container and updates the button text.
       */
      toggle() {
        if (this.button.getAttribute('aria-expanded') === 'true') {
          this.button.setAttribute('aria-expanded', 'false');
          this.button.textContent = this.readMore;
        } else {
          this.button.setAttribute('aria-expanded', 'true');
          this.button.textContent = this.readLess;
        }
      }
    },
  );
}
